// Global Instructions Rule Applied!
import React, { useState, useMemo, useEffect, useRef, useCallback } from 'react'
import { Checkbox, Typography, Tooltip, Row, Col } from 'antd'
import Button from '../../../core/components/Button.jsx'
import { FormSection } from '../../../core/components/FormSection.tsx'
import UserIconList from '../../../core/components/UserIconList.jsx'
import LabelDateBadge from '../../../core/components/LabelDateBadge.jsx'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faSquareCheck, faSquare, faPlus, faArrowsRotate, faListCheck, faList } from '@fortawesome/free-solid-svg-icons'
import { useDrag, useDrop } from 'react-dnd'
import { useSelector } from 'react-redux'
import { useAppDispatch } from '../../../state_manager/hooks/useAppDispatch'
import { updateTask, deleteTask, reorderTasks, localReorderTasks } from '../../../store/slices/tasksSlice'
import { DealFields } from '../../../state_manager/data_models/deal_record.ts'
import { selectDealById } from '../../../state_manager/state_update_actions/deals/selectors.ts'
import { UNKNOWN_CONTACT } from '../../../state_manager/constants/strings.ts'
import { selectCompanyById } from '../../../state_manager/state_update_actions/companies/selectors.ts'
import { selectProspectById } from '../../../state_manager/state_update_actions/fundraising/selectors.ts'
import { selectActiveRecordById } from '../../../state_manager/state_update_actions/active_records/selectors.ts'
import { AccountRecordFields } from '../../../state_manager/data_models/account_record.ts'
import { TaskRecordFields, TaskRecord } from '../../../state_manager/data_models/task_record.ts'
import { UserEntryFields } from '../../../state_manager/data_models/user_entry.ts'
import { fetchTasksForDeal, fetchTasksForAccount, fetchTasksForProspect } from '../services/tasksService.ts'
import { sortTasksCanonical } from '../utils/taskSortUtils.ts'
import { getAccessToken } from '../../../core/auth/authService'
import { store } from '../../../state_manager/store.ts'
import { updateDealRecord } from '../../../state_manager/state_update_actions/deals/index.ts'
import { updateCompanyRecord } from '../../../state_manager/state_update_actions/companies/index.ts'
import { updateProspectRecord } from '../../../state_manager/state_update_actions/fundraising/index.ts'
import { taskDisplaySubject } from '../utils/mergeTaskUpdate.ts'

const { Text } = Typography

// Drag and drop item types
const ItemTypes = {
  TASK: 'task'
}

interface DropZoneProps {
  position: 'top' | 'bottom'
  onDrop: (draggedItem: any, position: 'top' | 'bottom') => void
  isDragging: boolean
}

// Drop Zone component for top and bottom of the list
const DropZone: React.FC<DropZoneProps> = ({ position, onDrop, isDragging }) => {
  const [{ isOverZone }, drop] = useDrop({
    accept: ItemTypes.TASK,
    drop: (draggedItem) => {
      onDrop(draggedItem, position)
    },
    collect: (monitor) => ({
      isOverZone: monitor.isOver()
    })
  })

  // Only show the drop zone when actively dragging
  if (!isDragging) {
    return null
  }

  return (
    <div
      ref={(node) => {
        drop(node)
      }}
      className={`h-2 flex items-center justify-center transition-all duration-200 ${isOverZone ? 'bg-blue-50' : ''}`}
      data-debug={`drop-zone-${position}`}
    >
      {isOverZone && (
        <div
          className='w-full h-0.5 bg-blue-500 rounded-full shadow-blue-300'
          style={{ boxShadow: '0 0 6px rgba(59, 130, 246, 0.4)' }}
        />
      )}
    </div>
  )
}

interface TaskCardProps {
  task: TaskRecord
  index: number
  onTaskUpdate?: (taskId: string, completed: boolean) => void
  onTaskSelect?: (task: TaskRecord) => void
  onTaskDelete?: (taskId: string) => void
  selectedTaskId?: string | null
  moveTask: (dragIndex: number, hoverIndex: number) => void
  onView?: (task: TaskRecord) => void
  onDragStart?: () => void
  onDragEnd?: () => void
  onTaskDrop?: (draggedItem: any, dropIndex: number) => void
}

// TaskCard component with React DnD drag functionality
const TaskCard: React.FC<TaskCardProps> = ({
  task,
  index,
  onTaskUpdate,
  onTaskSelect,
  onTaskDelete,
  selectedTaskId,
  moveTask,
  onView,
  onDragStart,
  onDragEnd,
  onTaskDrop
}) => {
  // Get contact names from prog__task_assignees array
  // Memoized to prevent unnecessary re-renders
  const contactNames = useMemo(() => {
    const assignees = task[TaskRecordFields.prog__task_assignees] || []

    if (assignees.length === 0) return []

    return assignees.map((assignee) => {
      const id = assignee[UserEntryFields.contacts__id]
      const displayName = assignee[UserEntryFields.prog__display_name]
      const firstName = assignee[UserEntryFields.contacts__first_name] || ''
      const lastName = assignee[UserEntryFields.contacts__last_name] || ''
      const preferredName = assignee[UserEntryFields.contacts__preferred_name]
      const email = assignee[UserEntryFields.contacts__email]

      return {
        id,
        name: displayName || `${firstName} ${lastName}`.trim() || preferredName || email || UNKNOWN_CONTACT
      }
    })
  }, [task])
  const [isReady, setIsReady] = useState(false)
  const ref = useRef<HTMLDivElement>(null)

  // Minimal initialization to prevent first-drag issues
  useEffect(() => {
    const timer = setTimeout(() => setIsReady(true), 100)
    return () => clearTimeout(timer)
  }, [])

  const [{ isDragging }, drag] = useDrag({
    type: ItemTypes.TASK,
    item: () => {
      onDragStart?.()
      return { taskId: task[TaskRecordFields.tasks__id], index }
    },
    canDrag: !task[TaskRecordFields.tasks__completed], // Only allow dragging of incomplete tasks
    end: () => {
      onDragEnd?.()
    },
    collect: (monitor) => ({
      isDragging: monitor.isDragging()
    })
  })

  const [{ isOver, dropPosition }, drop] = useDrop({
    accept: ItemTypes.TASK,
    hover: (draggedItem: any, monitor) => {
      if (!ref.current || !isReady || task[TaskRecordFields.tasks__completed]) return

      const dragIndex = draggedItem.index
      const hoverIndex = index

      // Don't replace items with themselves
      if (dragIndex === hoverIndex) return

      // Determine rectangle on screen
      const hoverBoundingRect = ref.current.getBoundingClientRect()

      // Get vertical middle
      const hoverMiddleY = (hoverBoundingRect.bottom - hoverBoundingRect.top) / 2

      // Determine mouse position
      const clientOffset = monitor.getClientOffset()

      if (!clientOffset) return

      // Get pixels to the top
      const hoverClientY = clientOffset.y - hoverBoundingRect.top

      // Only perform the move when the mouse has crossed half of the items height
      // When dragging downwards, only move when the cursor is below 50%
      // When dragging upwards, only move when the cursor is above 50%

      // Dragging downwards
      if (dragIndex < hoverIndex && hoverClientY < hoverMiddleY) {
        return
      }

      // Dragging upwards
      if (dragIndex > hoverIndex && hoverClientY > hoverMiddleY) {
        return
      }

      // Time to actually perform the action
      moveTask(dragIndex, hoverIndex)

      // Note: we're mutating the monitor item here!
      // Generally it's better to avoid mutations,
      // but it's good here for the sake of performance
      // to avoid expensive index searches.
      draggedItem.index = hoverIndex
    },
    drop: (draggedItem: any) => {
      // Call the drop handler to persist changes
      onTaskDrop?.(draggedItem, index)
    },
    collect: (monitor) => {
      const clientOffset = monitor.getClientOffset()
      const boundingRect = ref.current?.getBoundingClientRect()
      let dropPosition: 'above' | 'below' | null = null

      if (monitor.isOver() && clientOffset && boundingRect) {
        const hoverMiddleY = (boundingRect.bottom - boundingRect.top) / 2
        const hoverClientY = clientOffset.y - boundingRect.top
        dropPosition = hoverClientY < hoverMiddleY ? 'above' : 'below'
      }

      return {
        isOver: monitor.isOver(),
        dropPosition
      }
    }
  })

  // Connect drag and drop
  drag(drop(ref))

  const handleTaskToggle = (taskId: string, completed: boolean) => {
    onTaskUpdate?.(taskId, completed)
  }

  const taskId = task[TaskRecordFields.tasks__id]
  const taskCompleted = task[TaskRecordFields.tasks__completed]
  const taskSubject = taskDisplaySubject(task)
  const taskDueBy = task[TaskRecordFields.tasks__due_by]

  return (
    <div
      data-debug={`task-wrapper-${taskId}`}
      data-task-id={taskId}
      data-task-completed={taskCompleted}
      className='relative'
    >
      {/* Drop indicator line */}
      {isOver && dropPosition && (
        <div
          data-debug={`task-drop-indicator-${taskId}`}
          className={`absolute left-0 right-0 h-1 z-10 rounded-full bg-blue-500 shadow-blue-300 ${
            dropPosition === 'above' ? '-top-2' : '-bottom-2'
          }`}
          style={{
            boxShadow: '0 0 8px rgba(59, 130, 246, 0.3)'
          }}
        />
      )}

      <div
        ref={ref}
        data-debug={`task-card-content-${taskId}`}
        className={`flex items-center space-x-2 p-1.5 rounded-md border shadow-sm transition-all duration-200 ${
          taskCompleted ? 'bg-gray-50 border-gray-300' : 'bg-white hover:border-blue-300'
        } ${isDragging ? 'opacity-50 scale-95' : ''} ${
          selectedTaskId === taskId ? 'border-blue-500 border-2 shadow-md' : 'border-gray-200'
        } ${!taskCompleted && !isDragging ? 'cursor-move' : ''}`}
        title={taskCompleted ? 'Completed task' : 'Drag to reorder'}
      >
        {/* Task Checkbox */}
        <div data-debug={`task-checkbox-section-${taskId}`} className='flex items-center'>
          <Checkbox
            data-debug={`task-checkbox-${taskId}`}
            checked={taskCompleted || false}
            onChange={(e) => {
              e.stopPropagation() // Prevent task selection when clicking checkbox
              handleTaskToggle(taskId || '', e.target.checked)
            }}
            className='task-completion-checkbox'
          />
        </div>

        {/* Task Content */}
        <div data-debug={`task-content-${taskId}`} className='flex-1 min-w-0'>
          <Row gutter={[8, 8]} align='middle' wrap data-debug={`task-header-${taskId}`}>
            <Col xs={24} lg={{ flex: '1 1 auto' }} data-debug={`task-title-section-${taskId}`}>
              <p
                data-debug={`task-title-${taskId}`}
                className={`text-[14px] font-medium cursor-pointer hover:text-blue-600 transition-colors m-0 ${
                  taskCompleted ? 'line-through text-gray-500 hover:text-gray-700' : 'text-gray-900'
                }`}
                title={`Click to view details: ${taskSubject || ''}`}
                onClick={(e) => {
                  e.stopPropagation()
                  onView?.(task)
                }}
              >
                {taskSubject || ''}
              </p>
            </Col>

            {/* Assignee Icon and Actions */}
            <Col xs={24} lg={{ flex: '0 0 auto' }} data-debug={`task-assignees-section-${taskId}`}>
              <Row gutter={[8, 8]} align='middle' wrap>
                {/* Assignee Avatars Group */}
                {contactNames.length > 0 && (
                  <Col flex='none'>
                    <UserIconList
                      contactNames={contactNames.map((contact) => contact.name)}
                      contactIds={contactNames.map((contact) => contact.id || '')}
                      maxVisible={5}
                      size='medium'
                    />
                  </Col>
                )}

                {/* Due Date Badge */}
                {taskDueBy && (
                  <Col flex='none'>
                    <LabelDateBadge date={taskDueBy} size='default' textSize='10px' />
                  </Col>
                )}
              </Row>
            </Col>
          </Row>
        </div>
      </div>
    </div>
  )
}

interface TasksSectionProps {
  dealId?: string | null
  recordId?: string | null
  recordType?: 'deal' | 'company' | 'prospect'
  onTaskSelect?: (task: TaskRecord) => void
  selectedTaskId?: string | null
  onAddTask?: () => void
  onEditModeChange?: (editing: boolean) => void
  onRefreshRequest?: (refreshFn: () => Promise<void>) => void
}

/**
 * Tasks Section Component
 * Displays tasks with completion tracking, reordering, and filtering
 * Supports deals, accounts, and prospects
 */
export function TasksSection({
  dealId,
  recordId,
  recordType = 'deal',
  onTaskSelect,
  selectedTaskId,
  onAddTask,
  onEditModeChange,
  onRefreshRequest
}: TasksSectionProps) {
  // Use recordId if provided, otherwise fall back to dealId for backward compatibility
  const entityId = recordId || dealId || ''
  const entityType = recordId ? recordType : 'deal'
  const [isDragging, setIsDragging] = useState(false)
  const [showCompleted, setShowCompleted] = useState(true)
  const [fetchedTasks, setFetchedTasks] = useState<TaskRecord[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const hasFetchedRef = useRef(false)
  const dispatch = useAppDispatch()

  // Get deal, company, or prospect from state using selector
  // For deals, check both deals Redux state and activeRecords (fallback)
  const dealRecordFromData = useSelector((state: any) =>
    entityType === 'deal' ? selectDealById(state, entityId) : null
  )
  const dealRecordFromActive = useSelector((state: any) =>
    entityType === 'deal' ? selectActiveRecordById(state, entityId) : null
  )
  const dealRecord = dealRecordFromData || dealRecordFromActive
  // For companies, check both companies.data and activeRecords (fallback)
  const companyRecordFromData = useSelector((state: any) =>
    entityType === 'company' ? selectCompanyById(state, entityId) : null
  )
  const companyRecordFromActive = useSelector((state: any) =>
    entityType === 'company' ? selectActiveRecordById(state, entityId) : null
  )
  const companyRecord = companyRecordFromData || companyRecordFromActive
  // For prospects, check both fundraising Redux state and activeRecords (fallback)
  const prospectRecordFromData = useSelector((state: any) =>
    entityType === 'prospect' ? selectProspectById(state, entityId) : null
  )
  const prospectRecordFromActive = useSelector((state: any) =>
    entityType === 'prospect' ? selectActiveRecordById(state, entityId) : null
  )
  const prospectRecord = prospectRecordFromData || prospectRecordFromActive

  // Get tasks from deal, company, or prospect object (default) - filter out completed tasks
  const dealTasks = useMemo(() => {
    if (entityType === 'deal' && dealRecord) {
      const allTasks: TaskRecord[] = dealRecord[DealFields.prog__tasks] || []
      // Filter out completed tasks by default (only show incomplete tasks)
      return allTasks.filter((task: TaskRecord) => !task[TaskRecordFields.tasks__completed])
    } else if (entityType === 'company' && companyRecord) {
      const allTasks: TaskRecord[] = companyRecord[AccountRecordFields.prog__tasks] || []
      // Filter out completed tasks by default (only show incomplete tasks)
      return allTasks.filter((task: TaskRecord) => !task[TaskRecordFields.tasks__completed])
    } else if (entityType === 'prospect' && prospectRecord) {
      // Read from both 'prog__tasks' and 'tasks' to handle API response variations
      const allTasks: TaskRecord[] = prospectRecord['prog__tasks'] || prospectRecord['tasks'] || []
      // Filter out completed tasks by default (only show incomplete tasks)
      return allTasks.filter((task: TaskRecord) => !task[TaskRecordFields.tasks__completed])
    }
    return []
  }, [dealRecord, companyRecord, prospectRecord, entityType])

  // Use fetched tasks when showCompleted is true, otherwise use filtered deal tasks
  const tasks = useMemo(() => {
    if (showCompleted) {
      return fetchedTasks
    }
    return dealTasks
  }, [showCompleted, fetchedTasks, dealTasks])

  // Reset fetched state when record changes (e.g. opening a different deal in drawer)
  useEffect(() => {
    hasFetchedRef.current = false
    setFetchedTasks([])
  }, [entityId])

  // Fetch tasks when showCompleted becomes true or when recordType is 'company'
  useEffect(() => {
    if (!entityId) {
      hasFetchedRef.current = false
      return
    }

    // For companies/prospects, fetch from API only if record doesn't have tasks or when showCompleted is true
    // For deals, only fetch when showCompleted is true
    const companyHasTasks =
      entityType === 'company' && companyRecord && (companyRecord[AccountRecordFields.prog__tasks] || []).length > 0
    const prospectHasTasks =
      entityType === 'prospect' &&
      prospectRecord &&
      (prospectRecord['prog__tasks'] || prospectRecord['tasks'] || []).length > 0
    const shouldFetch =
      (entityType === 'company' && !companyHasTasks) ||
      (entityType === 'prospect' && !prospectHasTasks) ||
      (showCompleted && !hasFetchedRef.current)

    if (shouldFetch) {
      const fetchTasks = async () => {
        hasFetchedRef.current = true
        setLoading(true)
        setError(null)
        try {
          const currentToken = getAccessToken(store.getState().auth)
          let fetched: TaskRecord[]
          if (entityType === 'company') {
            fetched = await fetchTasksForAccount(entityId, { include_completed: showCompleted }, currentToken)
          } else if (entityType === 'prospect') {
            fetched = await fetchTasksForProspect(entityId, { include_completed: showCompleted }, currentToken)
          } else {
            fetched = await fetchTasksForDeal(entityId, { include_completed: true }, currentToken)
          }
          setFetchedTasks(fetched)
        } catch (err) {
          console.error('Error fetching tasks:', err)
          setError(err instanceof Error ? err.message : 'Failed to fetch tasks')
          hasFetchedRef.current = false
        } finally {
          setLoading(false)
        }
      }

      fetchTasks()
    } else if (!showCompleted) {
      // Reset fetched flag when filter is disabled so we fetch fresh data when toggling back on
      // (Deals, companies, and prospects all need this to avoid showing stale completion state)
      hasFetchedRef.current = false
      setFetchedTasks([])
    }
  }, [entityId, entityType, showCompleted, companyRecord, prospectRecord])

  const handleTaskToggle = async (taskId: string, completed: boolean) => {
    try {
      // Optimistic update for immediate UI feedback
      const applyOptimisticUpdate = (taskList: TaskRecord[]) => {
        const updated = taskList.map((t) => {
          if (t[TaskRecordFields.tasks__id] === taskId) {
            return {
              ...t,
              [TaskRecordFields.tasks__completed]: completed,
              [TaskRecordFields.tasks__completed_on]: completed ? new Date().toISOString().split('T')[0] : null
            }
          }
          return t
        })
        return sortTasksCanonical(updated)
      }

      if (entityType === 'deal' && dealRecord) {
        const allTasks = dealRecord[DealFields.prog__tasks] || []
        const updatedTasks = applyOptimisticUpdate(allTasks)
        dispatch(updateDealRecord({ dealId: entityId, updates: { [DealFields.prog__tasks]: updatedTasks } }))
        if (showCompleted && hasFetchedRef.current) {
          setFetchedTasks(updatedTasks)
        }
      } else if (entityType === 'company' && companyRecord) {
        const allTasks = companyRecord[AccountRecordFields.prog__tasks] || []
        const updatedTasks = applyOptimisticUpdate(allTasks)
        dispatch(
          updateCompanyRecord({ companyId: entityId, updates: { [AccountRecordFields.prog__tasks]: updatedTasks } })
        )
        if (showCompleted && hasFetchedRef.current) {
          setFetchedTasks(updatedTasks)
        }
      } else if (entityType === 'prospect' && prospectRecord) {
        const allTasks = prospectRecord['prog__tasks'] || prospectRecord['tasks'] || []
        const updatedTasks = applyOptimisticUpdate(allTasks)
        dispatch(updateProspectRecord({ prospectId: entityId, updates: { prog__tasks: updatedTasks } }))
        if (showCompleted && hasFetchedRef.current) {
          setFetchedTasks(updatedTasks)
        }
      }

      // Get the complete task object from the full record (not filtered tasks array)
      // This ensures we can find the task even if it's currently filtered out (e.g., completed tasks when showCompleted is false)
      let existingTask: TaskRecord | undefined
      if (entityType === 'deal' && dealRecord) {
        const allTasks: TaskRecord[] = dealRecord[DealFields.prog__tasks] || []
        existingTask = allTasks.find((t) => t[TaskRecordFields.tasks__id] === taskId)
      } else if (entityType === 'company' && companyRecord) {
        const allTasks: TaskRecord[] = companyRecord[AccountRecordFields.prog__tasks] || []
        existingTask = allTasks.find((t) => t[TaskRecordFields.tasks__id] === taskId)
      } else if (entityType === 'prospect' && prospectRecord) {
        const allTasks: TaskRecord[] = prospectRecord['prog__tasks'] || prospectRecord['tasks'] || []
        existingTask = allTasks.find((t) => t[TaskRecordFields.tasks__id] === taskId)
      }

      if (!existingTask) {
        console.error('Task not found for toggle')
        return
      }

      // Merge existing task data with completion update
      const completeTaskData = {
        ...existingTask,
        [TaskRecordFields.tasks__id]: taskId,
        [TaskRecordFields.tasks__completed]: completed,
        [TaskRecordFields.tasks__completed_on]: completed ? new Date().toISOString().split('T')[0] : null
      }

      // Remove relationship fields - they shouldn't be sent when just updating completion
      delete completeTaskData['task_deals__deal_id']
      delete completeTaskData['task_deals__id']
      delete completeTaskData['task_accounts__account_id']
      delete completeTaskData['task_accounts__id']
      delete completeTaskData['task_prospects__prospect_id']
      delete completeTaskData['task_prospects__id']

      // Update task in backend - the Redux action will handle updating the state with the response
      await (
        dispatch(
          (updateTask as any)({
            dealId: entityId,
            taskId,
            taskData: completeTaskData,
            recordType: entityType
          })
        ) as any
      ).unwrap()

      // Refresh fetchedTasks from Redux state after API update (thunk already updated the record)
      // Ensures we have the latest from API and correct canonical sort order
      if (showCompleted && hasFetchedRef.current) {
        const state = store.getState()
        const deal =
          entityType === 'deal' && (selectDealById(state, entityId) || selectActiveRecordById(state, entityId))
        const company =
          entityType === 'company' && (selectCompanyById(state, entityId) || selectActiveRecordById(state, entityId))
        const prospect =
          entityType === 'prospect' && (selectProspectById(state, entityId) || selectActiveRecordById(state, entityId))
        const allTasks =
          deal?.[DealFields.prog__tasks] ||
          company?.[AccountRecordFields.prog__tasks] ||
          prospect?.['prog__tasks'] ||
          prospect?.['tasks'] ||
          []
        setFetchedTasks(sortTasksCanonical([...allTasks]))
      }
    } catch (error) {
      // Error handling is already done in the updateTask action
    }
  }

  const handleAddTask = () => {
    onAddTask?.()
  }

  const handleTaskView = (task: TaskRecord) => {
    onTaskSelect?.(task)
  }

  const toggleShowCompleted = () => {
    setShowCompleted(!showCompleted)
  }

  const handleRefresh = useCallback(async () => {
    if (!entityId) return

    setLoading(true)
    setError(null)
    try {
      const currentToken = getAccessToken(store.getState().auth)
      if (!currentToken) {
        setError('No access token available')
        return
      }

      const includeCompleted = true
      let fetched: TaskRecord[]
      if (entityType === 'company') {
        fetched = await fetchTasksForAccount(entityId, { include_completed: includeCompleted }, currentToken)
      } else if (entityType === 'prospect') {
        fetched = await fetchTasksForProspect(entityId, { include_completed: includeCompleted }, currentToken)
      } else {
        fetched = await fetchTasksForDeal(entityId, { include_completed: includeCompleted }, currentToken)
      }

      if (entityType === 'deal') {
        dispatch(updateDealRecord({ dealId: entityId, updates: { [DealFields.prog__tasks]: fetched } }))
      } else if (entityType === 'company') {
        dispatch(updateCompanyRecord({ companyId: entityId, updates: { [AccountRecordFields.prog__tasks]: fetched } }))
      } else {
        dispatch(updateProspectRecord({ prospectId: entityId, updates: { prog__tasks: fetched } }))
      }

      hasFetchedRef.current = true
      setFetchedTasks(fetched)
    } catch (err) {
      console.error('Error refreshing tasks:', err)
      setError(err instanceof Error ? err.message : 'Failed to refresh tasks')
      hasFetchedRef.current = false
    } finally {
      setLoading(false)
    }
  }, [entityId, entityType, dispatch])

  const refreshRef = useRef<(() => Promise<void>) | null>(null)
  refreshRef.current = handleRefresh

  useEffect(() => {
    if (onRefreshRequest && refreshRef.current) {
      onRefreshRequest(refreshRef.current)
    }
  }, [onRefreshRequest])

  const handleDragStart = () => {
    setIsDragging(true)
  }

  const handleDragEnd = () => {
    setIsDragging(false)
  }

  // Handle drop zone drops (top and bottom of the list)
  const handleDropZoneDrop = (draggedItem: any, position: 'top' | 'bottom') => {
    const dragTask = tasks.find((task) => task[TaskRecordFields.tasks__id] === draggedItem.taskId)
    if (!dragTask) return

    const dragTaskId = dragTask[TaskRecordFields.tasks__id]
    const displayTasks = sortTasksCanonical([...tasks])
    const withoutDrag = displayTasks.filter((t) => t[TaskRecordFields.tasks__id] !== dragTaskId)

    const newOrder = position === 'top' ? [dragTask, ...withoutDrag] : [...withoutDrag, dragTask]

    const openTasksInOrder = newOrder.filter((t) => !t[TaskRecordFields.tasks__completed])
    const taskOrders: Array<{ id: string; sort_order: number }> = openTasksInOrder.map((t, i) => ({
      id: t[TaskRecordFields.tasks__id] || '',
      sort_order: i + 1
    }))

    const newTasks = tasks.map((t) => {
      const order = taskOrders.find((o) => o.id === t[TaskRecordFields.tasks__id])
      if (order && !t[TaskRecordFields.tasks__completed]) {
        return { ...t, [TaskRecordFields.tasks__sort_order]: order.sort_order }
      }
      return t
    })

    if (showCompleted || entityType === 'company') {
      setFetchedTasks(newTasks)
    } else {
      dispatch(localReorderTasks({ dealId: entityId, newTaskOrder: newTasks }))
    }

    dispatch(reorderTasks({ dealId: entityId, taskOrders }))
  }

  // Handle task drops between other tasks
  const handleTaskDrop = (draggedItem: any, dropIndex: number) => {
    const dragTask = tasks.find((task) => task[TaskRecordFields.tasks__id] === draggedItem.taskId)
    if (!dragTask) return

    const dragTaskId = dragTask[TaskRecordFields.tasks__id]

    // Build new order: remove dragged task, insert at dropIndex
    const displayTasks = sortTasksCanonical([...tasks])
    const withoutDrag = displayTasks.filter((t) => t[TaskRecordFields.tasks__id] !== dragTaskId)
    const newOrder = [...withoutDrag.slice(0, dropIndex), dragTask, ...withoutDrag.slice(dropIndex)]

    // Assign sort_order 1, 2, 3, ... to all open tasks in the new order
    // This ensures correct placement when neighbors have null sort_order (canonical sort puts
    // sort_order before null, so assigning only the dragged task would put it first)
    const openTasksInOrder = newOrder.filter((t) => !t[TaskRecordFields.tasks__completed])
    const taskOrders: Array<{ id: string; sort_order: number }> = openTasksInOrder.map((t, i) => ({
      id: t[TaskRecordFields.tasks__id] || '',
      sort_order: i + 1
    }))

    const newTasks = tasks.map((t) => {
      const order = taskOrders.find((o) => o.id === t[TaskRecordFields.tasks__id])
      if (order && !t[TaskRecordFields.tasks__completed]) {
        return { ...t, [TaskRecordFields.tasks__sort_order]: order.sort_order }
      }
      return t
    })

    if (showCompleted || entityType === 'company') {
      setFetchedTasks(newTasks)
    } else {
      dispatch(localReorderTasks({ dealId: entityId, newTaskOrder: newTasks }))
    }

    dispatch(reorderTasks({ dealId: entityId, taskOrders }))
  }

  // Move task within the list using React DnD (only for visual feedback during hover)
  const moveTask = (dragIndex: number, hoverIndex: number) => {
    const displayTasks = sortTasksCanonical([...tasks])

    if (
      dragIndex === hoverIndex ||
      dragIndex < 0 ||
      hoverIndex < 0 ||
      dragIndex >= displayTasks.length ||
      hoverIndex >= displayTasks.length
    ) {
      return
    }

    const dragTask = displayTasks[dragIndex]
    const dragTaskId = dragTask[TaskRecordFields.tasks__id]
    if (!dragTask) return

    // Build new order: remove dragged from current position, insert at hover position
    const withoutDrag = displayTasks.filter((t) => t[TaskRecordFields.tasks__id] !== dragTaskId)
    const insertIndex = dragIndex < hoverIndex ? hoverIndex - 1 : hoverIndex
    const newOrder = [...withoutDrag.slice(0, insertIndex), dragTask, ...withoutDrag.slice(insertIndex)]

    // Assign sort_order 1, 2, 3, ... to all open tasks (same as handleTaskDrop)
    const openTasksInOrder = newOrder.filter((t) => !t[TaskRecordFields.tasks__completed])
    const newTasks = tasks.map((t) => {
      const idx = openTasksInOrder.findIndex((o) => o[TaskRecordFields.tasks__id] === t[TaskRecordFields.tasks__id])
      if (idx >= 0) {
        return { ...t, [TaskRecordFields.tasks__sort_order]: idx + 1 }
      }
      return t
    })

    if (showCompleted || entityType === 'company') {
      setFetchedTasks(newTasks)
    } else {
      dispatch(localReorderTasks({ dealId: entityId, newTaskOrder: newTasks }))
    }
  }

  const handleTaskDelete = async (taskId: string) => {
    try {
      await dispatch(deleteTask({ dealId: entityId, taskId, recordType: entityType })).unwrap()

      // Refresh fetched tasks if showing completed or if it's a company
      if ((showCompleted || entityType === 'company') && hasFetchedRef.current) {
        try {
          const currentToken = getAccessToken(store.getState().auth)
          const fetched =
            entityType === 'company'
              ? await fetchTasksForAccount(entityId, { include_completed: showCompleted }, currentToken)
              : await fetchTasksForDeal(entityId, { include_completed: true }, currentToken)
          setFetchedTasks(fetched)
        } catch (err) {
          console.error('Error refreshing tasks:', err)
        }
      }
    } catch (error) {
      // Error handling is already done in the deleteTask action
    }
  }

  // Canonical sort: open first (sort_order or created_at), completed second (completed_on DESC)
  const displayTasks = sortTasksCanonical([...tasks])

  const headerActions = (
    <div className='flex items-center gap-1 flex-shrink-0'>
      <Tooltip title={showCompleted ? 'Hide completed tasks' : 'Show completed tasks'}>
        <Button
          variant='default'
          size='small'
          icon={
            <FontAwesomeIcon
              icon={showCompleted ? faListCheck : faList}
              style={{
                color: showCompleted ? '#1890ff' : '#9ca3af',
                fontSize: '15px'
              }}
            />
          }
          onClick={toggleShowCompleted}
        />
      </Tooltip>
      <Tooltip title='Refresh tasks'>
        <Button
          variant='default'
          size='small'
          icon={<FontAwesomeIcon icon={faArrowsRotate} style={{ color: '#9ca3af' }} />}
          style={{ color: '#9ca3af' }}
          loading={loading}
          onClick={handleRefresh}
        />
      </Tooltip>
      <Tooltip title='Add new task'>
        <Button
          variant='primary'
          size='small'
          className='text-xs px-2 py-1 h-6'
          icon={<FontAwesomeIcon icon={faPlus} className='text-xs' />}
          onClick={handleAddTask}
        />
      </Tooltip>
    </div>
  )

  return (
    <FormSection
      title='Tasks'
      titleColor='green'
      headerActions={
        <div className='flex items-center gap-1.5 flex-shrink-0 min-w-0'>
          {!selectedTaskId && (
            <div
              data-debug='tasks-count-display'
              className='text-xs text-gray-500 whitespace-nowrap flex-shrink-0 hidden lg:block'
            >
              {displayTasks.length} task{displayTasks.length > 1 ? 's' : displayTasks.length === 0 ? 's' : ''}
            </div>
          )}
          {headerActions}
        </div>
      }
      loading={loading}
      className='shadow-sm border border-gray-200 pb-1'
    >
      <div
        data-debug='tasks-container'
        data-component='TasksSection'
        data-record-id={entityId}
        data-record-type={entityType}
        className='overflow-y-auto tasks-section-container max-h-[150px] mt-1'
      >
        {error ? (
          <div data-debug='tasks-error-state' className='text-center py-8'>
            <Text type='danger'>{error}</Text>
          </div>
        ) : displayTasks.length === 0 ? (
          <div data-debug='tasks-empty-state' className='text-center py-8'>
            <Text type='secondary'>No tasks yet</Text>
          </div>
        ) : (
          <div data-debug='tasks-list' className='space-y-1'>
            {/* Top drop zone */}
            <DropZone position='top' onDrop={handleDropZoneDrop} isDragging={isDragging} />

            {displayTasks.map((task, index) => (
              <TaskCard
                key={task[TaskRecordFields.tasks__id]}
                data-debug={`task-card-${task[TaskRecordFields.tasks__id]}`}
                task={task}
                index={index}
                onTaskUpdate={handleTaskToggle}
                onTaskSelect={onTaskSelect}
                onTaskDelete={handleTaskDelete}
                selectedTaskId={selectedTaskId}
                moveTask={moveTask}
                onView={handleTaskView}
                onDragStart={handleDragStart}
                onDragEnd={handleDragEnd}
                onTaskDrop={handleTaskDrop}
              />
            ))}

            {/* Bottom drop zone */}
            <DropZone position='bottom' onDrop={handleDropZoneDrop} isDragging={isDragging} />
          </div>
        )}
      </div>
    </FormSection>
  )
}
